import React from "react";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "../../../pages/Home/UserContext";
import { useNotification } from "../../../components/Notification/NotificationManager";

import { useAttendanceDashboard } from "./hooks/useAttendanceDashboard";

import AttendanceDashboardHeader from "./components/AttendanceDashboardHeader";
import AttendanceGroupPickerPanel from "./components/AttendanceGroupPickerPanel";
import AttendanceFiltersCard from "./components/AttendanceFiltersCard";
import AttendanceKpiCards from "./components/AttendanceKpiCards";
import AttendanceAdherentsGrid from "./components/AttendanceAdherentsGrid";
import AttendanceEmptyState from "./components/AttendanceEmptyState";
import AttendanceSkeleton from "./components/AttendanceSkeleton";
import AttendanceMonthlySection from "./components/AttendanceMonthlySection";

function UnauthorizedPanel() {
  return (
    <main className="min-h-screen bg-slate-50 pb-40 text-slate-950 dark:bg-slate-950 dark:text-white">
      <header className="relative overflow-hidden bg-slate-950 pt-[88px] lg:pt-[104px]">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-red-950/80 to-slate-950" />

        <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-20 pt-10 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-500/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-red-100 backdrop-blur-xl">
            Accès refusé
          </div>

          <h1 className="mt-5 font-titre text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Présence des judokas
          </h1>

          <p className="mt-4 max-w-3xl text-sm font-medium leading-7 text-white/70 sm:text-base">
            Cette page est réservée aux administrateurs et aux coachs.
          </p>
        </div>
      </header>

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

function AttendanceDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const { addNotification } = useNotification();

  const {
    authorized,
    groupCounts,

    groups,
    selectedGroup,
    filteredAdherents,

    filters,
    setFilters,
    selectGroup,
    changeGroup,

    attendance,
    assiduityInfo,
    birthdaySet,
    stats,

    loading,
    refreshing,
    savingIds,

    monthly,

    refresh,
    handleToggleAttendance,
    loadAssiduity,
  } = useAttendanceDashboard({ user, addNotification });

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <main className="min-h-screen bg-slate-50 pb-40 text-slate-950 dark:bg-slate-950 dark:text-white">
      <AttendanceDashboardHeader
        stats={stats}
        refreshing={refreshing}
        onRefresh={refresh}
      />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {!selectedGroup ? (
          <AttendanceGroupPickerPanel
            loading={loading}
            groups={groups}
            groupCounts={groupCounts}
            filters={filters}
            setFilters={setFilters}
            onSelectGroup={selectGroup}
          />
        ) : (
          <AttendanceFiltersCard
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            selectedGroup={selectedGroup}
            onRefresh={refresh}
            onChangeGroup={changeGroup}
          />
        )}
      </section>

      {selectedGroup && (
        <>
          <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
            <AttendanceKpiCards stats={stats} loading={loading} />
          </section>

          <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
            {loading ? (
              <AttendanceSkeleton />
            ) : filteredAdherents.length > 0 ? (
              <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20">
                <div className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-200">
                      Appel du cours
                    </p>

                    <h2 className="mt-1 font-titre text-xl font-bold text-slate-950 dark:text-white">
                      {selectedGroup.nom}
                    </h2>

                    <p className="mt-1 text-xs font-bold text-slate-400">
                      Touchez une carte : non saisi → présent → absent → non
                      saisi.
                    </p>
                  </div>

                  <span className="inline-flex min-h-10 items-center justify-center rounded-2xl bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 dark:bg-sky-500/10 dark:text-sky-200">
                    {filteredAdherents.length} judoka
                    {filteredAdherents.length > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="p-4">
                  <AttendanceAdherentsGrid
                    adherents={filteredAdherents}
                    attendance={attendance}
                    savingIds={savingIds}
                    assiduityInfo={assiduityInfo}
                    birthdaySet={birthdaySet}
                    onToggle={handleToggleAttendance}
                    requestAssiduity={loadAssiduity}
                  />
                </div>
              </section>
            ) : (
              <AttendanceEmptyState />
            )}
          </section>

          <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
            <AttendanceMonthlySection
              selectedGroup={selectedGroup}
              selectedMonth={monthly.selectedMonth}
              rows={monthly.rows}
              kpis={monthly.kpis}
              highlightIds={monthly.highlightIds}
              loading={monthly.loading}
              savingCells={monthly.savingCells}
              onSelectMonth={monthly.setSelectedMonth}
              onToggleCell={monthly.handleToggleCell}
              onRefresh={monthly.refresh}
            />
          </section>
        </>
      )}

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default AttendanceDashboard;